import {
  CommonHelperFunctions,
  IActionDefinitionDto,
  IActionUsageReportDto,
  IAiAgentModel,
  IAiAgentTemplate,
  IAiModel,
  IAiTemplate,
  IAiTemplateRequest,
  IDatabaseSchemaDto,
  IDatabaseTableIdsResponseDto,
  IDatasourceDto,
  IDatasourceRuntimePin,
  IDbJoinDataDto,
  IGenericSelectDataModel,
  IRestApiPathExecuteDto,
  ISessionVariable,
  IVisionInferenceModel,
  IVisionTemplate,
  TestDBConnectionRequestDTO,
  IGenerateActionDescriptionsRequest,
  IGenerateActionDescriptionsResponse,
  ISaveActionDescriptionsRequest,
  ISaveActionDescriptionsResponse
} from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

const backendUrl = CommonHelperFunctions.GetBackendUrl();

export interface NativeActionExecutePackage {
  name: string;
  version: string;
}

export interface NativeActionExecuteRequest {
  code: string;
  className?: string;
  methodName: string;
  args: unknown[];
  projectId?: string;
  packages?: NativeActionExecutePackage[];
}

export interface NativeActionExecuteResult {
  ok: boolean;
  result?: unknown;
  errorType?: string;
  errorMessage?: string;
}

export interface NativeActionParameter {
  name: string;
  type: string;
  /** JSON template for complex/list params (present only when the param is an object or list). */
  template?: string;
}

export interface NativeActionMethod {
  name: string;
  parameters: NativeActionParameter[];
  returnType: string;
  isAsync: boolean;
}

export interface NativeActionParseResult {
  className?: string;
  methods: NativeActionMethod[];
}

export class DatasourceService {
  public static TestConnection(connectionString: string): Promise<AxiosResponse<any>> {
    try {
      return Axios.put<null, AxiosResponse>(`${backendUrl}/datasource/sqlserver/testconnection`, { connectionString });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static TestSapConnection(connectionString: string): Promise<AxiosResponse<any>> {
    try {
      return Axios.put<null, AxiosResponse>(`${backendUrl}/datasource/sap/testconnection`, { connectionString });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static GetSapFunctionGroups(datasourceId: string, searchKey?: string): Promise<AxiosResponse<any>> {
    try {
      return Axios.get<null, AxiosResponse>(`${backendUrl}/datasource/sap/functiongroups`, {
        params: { datasourceId, searchKey }
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static GetSapFunctions(
    datasourceId: string,
    functionGroup?: string,
    searchKey?: string
  ): Promise<AxiosResponse<any>> {
    try {
      return Axios.get<null, AxiosResponse>(`${backendUrl}/datasource/sap/functions`, {
        params: { datasourceId, functionGroup, searchKey }
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static GetSapFunctionSignature(datasourceId: string, functionName: string): Promise<AxiosResponse<any>> {
    try {
      return Axios.get<null, AxiosResponse>(`${backendUrl}/datasource/sap/functionsignature`, {
        params: { datasourceId, functionName }
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static TestConnectionByType(payload: TestDBConnectionRequestDTO) {
    try {
      return Axios.put<null, AxiosResponse>(`${backendUrl}/datasource/sqlserver/testconnectionbytype`, payload);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static ExecuteQuery(action: IActionDefinitionDto, truncateResult: boolean = false) {
    try {
      return Axios.put<null, AxiosResponse>(
        `${backendUrl}/datasource/sqlserver/execute?truncateResult=${truncateResult}`,
        action
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetSchema(
    datasourceId: string,
    withObjectId: boolean = false,
    fromCache: boolean = true
  ): Promise<AxiosResponse<IDatabaseSchemaDto>> {
    try {
      return await Axios.put<null, AxiosResponse>(
        `${backendUrl}/datasource/sqlserver/schema`,
        { id: datasourceId },
        { params: { withObjectId, fromCache } }
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  // KOA-17026 — slim list endpoint. Returns only [{id, name, schema}] per
  // table. reload_datasources uses this to skip the multi-MB column-metadata
  // payload that triggers body-timeout on tenants with many tables. Detail
  // for any specific table is fetched on demand via `GetSchemaForTables`.
  public static async GetTableIds(datasourceId: string): Promise<AxiosResponse<IDatabaseTableIdsResponseDto>> {
    try {
      return await Axios.put<null, AxiosResponse>(`${backendUrl}/datasource/sqlserver/schema/tables`, {
        id: datasourceId
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  // KOA-17026 — detail-by-name companion. Returns the full schema restricted
  // to the requested tableNames. Consumers (sql-action editor, managed-db
  // save panel) call this when they actually need column metadata.
  public static async GetSchemaForTables(
    datasourceId: string,
    tableNames: string[],
    fromCache: boolean = true
  ): Promise<AxiosResponse<IDatabaseSchemaDto>> {
    try {
      return await Axios.put<null, AxiosResponse>(
        `${backendUrl}/datasource/sqlserver/schema/tables/details`,
        { id: datasourceId, tableNames },
        { params: { fromCache } }
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static SaveDatasource(datasource: IDatasourceDto): Promise<AxiosResponse<any>> {
    try {
      return Axios.post<null, AxiosResponse>(`${backendUrl}/datasource`, datasource);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static ExecuteApiEndpoint(datasource: IRestApiPathExecuteDto): Promise<AxiosResponse<any>> {
    try {
      return Axios.post<null, AxiosResponse>(`${backendUrl}/datasource/restnoswagger/execute`, datasource);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async SaveAction(action: IActionDefinitionDto): Promise<AxiosResponse<any>> {
    try {
      return await Axios.post<null, AxiosResponse>(`${backendUrl}/datasource/action`, action);
    } catch (error: any) {
      if (error?.response?.data?.message?.includes("already exist")) {
        KMainFunctions.displayWarningNotification(error?.response?.data?.message);
      } else {
        KMainFunctions.exceptionHandler(error);
      }
      return error;
    }
  }

  public static async ParseTypeScript(code: string): Promise<
    AxiosResponse<{
      className: string;
      methods: Array<{
        name: string;
        parameters: Array<{ name: string; type: string }>;
        returnType: string;
        isAsync: boolean;
      }>;
    }>
  > {
    return Axios.post(`${backendUrl}/datasource/typescript/parse`, { code });
  }

  public static async ExecuteTypeScript(payload: {
    code: string;
    className: string;
    methodName: string;
    args: unknown[];
    serverSide: boolean;
  }): Promise<AxiosResponse<{ result?: unknown; transpiledCode?: string; className?: string; methodName?: string }>> {
    return Axios.post(`${backendUrl}/datasource/typescript/execute`, payload);
  }

  public static async ExecutePython(
    payload: NativeActionExecuteRequest
  ): Promise<AxiosResponse<NativeActionExecuteResult>> {
    return Axios.post(`${backendUrl}/datasource/python/execute`, payload);
  }

  public static async ExecuteJava(
    payload: NativeActionExecuteRequest
  ): Promise<AxiosResponse<NativeActionExecuteResult>> {
    return Axios.post(`${backendUrl}/datasource/java/execute`, payload);
  }

  public static async ParsePython(code: string): Promise<AxiosResponse<NativeActionParseResult>> {
    return Axios.post(`${backendUrl}/datasource/python/parse`, { code });
  }

  public static async ParseJava(code: string): Promise<AxiosResponse<NativeActionParseResult>> {
    return Axios.post(`${backendUrl}/datasource/java/parse`, { code });
  }

  // Selectable runtime versions for a backend language, from the runtime catalog (single source of truth). Returns []
  // for non-runtime-hosted languages (C#/TypeScript/Go/Rust). The runtime-version pickers call this instead of
  // hard-coding their own copy of the list.
  public static async GetRuntimeVersions(language: string): Promise<AxiosResponse<IDatasourceRuntimePin[]>> {
    return Axios.get(`${backendUrl}/datasource/runtimes`, { params: { language } });
  }

  public static async ExecuteGo(
    payload: NativeActionExecuteRequest
  ): Promise<AxiosResponse<NativeActionExecuteResult>> {
    return Axios.post(`${backendUrl}/datasource/go/execute`, payload);
  }

  public static async ParseGo(code: string): Promise<AxiosResponse<NativeActionParseResult>> {
    return Axios.post(`${backendUrl}/datasource/go/parse`, { code });
  }

  public static async ExecuteRust(
    payload: NativeActionExecuteRequest
  ): Promise<AxiosResponse<NativeActionExecuteResult>> {
    return Axios.post(`${backendUrl}/datasource/rust/execute`, payload);
  }

  public static async ParseRust(code: string): Promise<AxiosResponse<NativeActionParseResult>> {
    return Axios.post(`${backendUrl}/datasource/rust/parse`, { code });
  }

  public static async ExecuteCSharp(
    payload: NativeActionExecuteRequest
  ): Promise<AxiosResponse<NativeActionExecuteResult>> {
    return Axios.post(`${backendUrl}/datasource/csharp/execute`, payload);
  }

  public static async ParseCSharp(code: string): Promise<AxiosResponse<NativeActionParseResult>> {
    return Axios.post(`${backendUrl}/datasource/csharp/parse`, { code });
  }

  public static async DeleteAction(action: IActionDefinitionDto): Promise<AxiosResponse<any>> {
    try {
      return await Axios.put<null, AxiosResponse>(`${backendUrl}/datasource/action/delete`, action);
    } catch (error: any) {
      // KMainFunctions.exceptionHandler(error);
      KMainFunctions.displayErrorNotification(error.response.data);
      return error;
    }
  }

  public static async GetDatasourcesOfDomain(): Promise<AxiosResponse<IDatasourceDto[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/datasource`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetActionsByDomain(): Promise<AxiosResponse<IActionDefinitionDto[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/datasource/actions`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetActionsByProjectId(projectId: string): Promise<AxiosResponse<IActionDefinitionDto[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/datasource/actions/byprojectid/${projectId}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  /**
   * KOA-17818 — every place the action is used, down to the element inside each screen, so the
   * impact of editing a SQL action is visible before the edit. Screens are scoped to the project
   * in the request header; jobs, workflows and API Builder endpoints are domain-wide.
   */
  public static async GetActionUsages(actionId: string): Promise<AxiosResponse<IActionUsageReportDto>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/datasource/action/usages`, {
        params: { actionId }
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetActionDetail(actionId: string): Promise<AxiosResponse<IActionDefinitionDto>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/datasource/action/detail`, {
        params: { actionId }
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetCSharpCodeTemplates(
    datasourcetype: any,
    datasourceName?: string
  ): Promise<AxiosResponse<any>> {
    try {
      // datasourceName: the backend names the seeded template class after the datasource so two
      // datasources created from the same template never collide on the hardcoded class name.
      const nameQuery: string = datasourceName ? `&datasourceName=${encodeURIComponent(datasourceName)}` : "";
      return await Axios.get<null, AxiosResponse>(
        `${backendUrl}/datasource/datasource/codetemplate?datasourcetype=${datasourcetype}${nameQuery}`
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async DeleteDatasource(id: string): Promise<AxiosResponse<any>> {
    try {
      return await Axios.delete<null, AxiosResponse>(`${backendUrl}/datasource/delete?datasourceId=${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GenerateSqlParamTypes(
    dsId: string,
    statement: string,
    parameters: any[]
  ): Promise<AxiosResponse<any>> {
    try {
      return await Axios.post<null, AxiosResponse>(`${backendUrl}/datasource/statement/info`, {
        kDatasource_Id: dsId,
        statement,
        parameters
      });
    } catch (error: any) {
      return error;
    }
  }

  public static async GetJoinData(tableName: string, tableSchema: string): Promise<AxiosResponse<IDbJoinDataDto[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(
        `${backendUrl}/datasource/getjoindata?tableName=${tableName}&tableSchema=${tableSchema}`
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async TestGenericSelect(
    datasourceId: string,
    data: IGenericSelectDataModel
  ): Promise<AxiosResponse<any[]>> {
    try {
      return await Axios.post<null, AxiosResponse>(`${backendUrl}/datasource/${datasourceId}/testgenericselect`, data);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetGenericSelectDataSql(
    datasourceId: string,
    data: IGenericSelectDataModel
  ): Promise<AxiosResponse<string>> {
    try {
      return await Axios.post<null, AxiosResponse>(
        `${backendUrl}/datasource/${datasourceId}/getgenericselectdatasql`,
        data
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetAiModels(): Promise<AxiosResponse<IAiModel[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/datasource/ai/model`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetAiModelNames(): Promise<AxiosResponse<string>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/datasource/ai/model/names`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetAiTemplates(modelNames?: string): Promise<AxiosResponse<IAiTemplate[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/datasource/ai/template`, { params: { modelNames } });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async SaveAiActionTemplate(request: IAiTemplateRequest): Promise<AxiosResponse<string>> {
    try {
      return await Axios.post<null, AxiosResponse>(`${backendUrl}/datasource/ai/template/saveaction`, request);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetSessionVariables(): Promise<AxiosResponse<ISessionVariable[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/SessionContextVariable`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async UpdateSessionVariables(
    variables: ISessionVariable[]
  ): Promise<AxiosResponse<ISessionVariable[]>> {
    try {
      return await Axios.post<null, AxiosResponse>(`${backendUrl}/SessionContextVariable`, variables);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static GetAgentModels() {
    return Axios.get<null, AxiosResponse<IAiAgentModel[]>>(`${backendUrl}/datasource/ai/agent/model`);
  }

  public static GetAgentFunctionCallingModels() {
    return Axios.get<null, AxiosResponse<IAiAgentModel[]>>(`${backendUrl}/datasource/ai/agent/function-calling-model`);
  }

  public static GetAgentEmbeddingModels() {
    return Axios.get<null, AxiosResponse<IAiAgentModel[]>>(`${backendUrl}/datasource/ai/agent/embedding-model`);
  }

  public static GetAgentFrameworks() {
    return Axios.get<null, AxiosResponse<IAiAgentModel[]>>(`${backendUrl}/datasource/ai/agent/framework`);
  }

  public static GetAgentTemplates() {
    return Axios.get<null, AxiosResponse<IAiAgentTemplate[]>>(`${backendUrl}/datasource/ai/agent/template`);
  }

  public static async SaveAiAgentTemplate(request: IAiTemplateRequest): Promise<AxiosResponse<string>> {
    try {
      return await Axios.post<null, AxiosResponse>(`${backendUrl}/datasource/ai/agent/template/saveaction`, request);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  // KOA-16736: AI-powered action description generation for KAction MCP tools
  public static async GenerateActionDescriptions(
    request: IGenerateActionDescriptionsRequest
  ): Promise<AxiosResponse<IGenerateActionDescriptionsResponse>> {
    try {
      return await Axios.post<null, AxiosResponse<IGenerateActionDescriptionsResponse>>(
        `${backendUrl}/datasource/ai/agent/action/generatedescriptions`,
        request
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async SaveActionDescriptions(
    request: ISaveActionDescriptionsRequest
  ): Promise<AxiosResponse<ISaveActionDescriptionsResponse>> {
    try {
      return await Axios.post<null, AxiosResponse<ISaveActionDescriptionsResponse>>(
        `${backendUrl}/datasource/ai/agent/action/savedescriptions`,
        request
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetVisionInferenceModels(): Promise<AxiosResponse<IVisionInferenceModel[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/datasource/ai/vision/inferencemodel`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetVisionTemplates(): Promise<AxiosResponse<IVisionTemplate[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/datasource/ai/vision/template`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async TestAiAgent(projectId: string, name: string, aiAgentInfo: any): Promise<AxiosResponse<any>> {
    try {
      return await Axios.post<null, AxiosResponse>(
        `${backendUrl}/projects/generateaiagentproject?projectId=${projectId}&name=${name}`,
        aiAgentInfo
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  public static async GetDatasourceActions(datasourceId: string): Promise<AxiosResponse<IActionDefinitionDto[]>> {
    try {
      return await Axios.get<null, AxiosResponse<IActionDefinitionDto[]>>(
        `${backendUrl}/datasource/${datasourceId}/actions`
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }
}
